import { Box, Grid, TextField, Typography } from '@mui/material';
import { IAddress } from '@pepperhq/types-sdk';
import { useFormik } from 'formik';
import React from 'react';
import { addressApi } from '../addressApi';
import * as Yup from 'yup';
import { AddressMapPicker } from './AddressMapPicker';
import { useTranslation } from 'react-i18next';
import { BaseModal } from 'ui/BaseModal';
import { ButtonV2 } from 'ui/ButtonV2';
import { FlatAppbar } from 'ui/FlatAppbar';
import { FlatToolbar } from 'ui/FlatToolbar';
import { useNotificationAlert } from 'ui/alert/useNotificationAlert';
import { NotificationAlert } from 'ui/alert/NotificationAlert';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';

interface AddressFormProps {
    onClose: () => void;
    onCreate: (data: IAddress) => void;
}

interface AddressFormData {
    label?: string;
    address: string;
    line2?: string;
    city: string;
    region?: string;
    postalCode: string;
    note?: string;
}

const formInitialValues: AddressFormData = {
    label: '',
    address: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    note: ''
};

const getAddressBodyFromAddressData = (data: AddressFormData) => ({
    line1: data.address,
    line2: data.line2 || undefined,
    city: data.city,
    region: data.region || undefined,
    postalCode: data.postalCode
});

export const AddressForm: React.FC<AddressFormProps> = ({ onClose, onCreate }) => {
    const { currentNotification, dismissAlert, showAlert } = useNotificationAlert();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { logUserEvent } = useGAHelpers();
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [mapOpen, setMapOpen] = React.useState(true);
    const [fullAddress, setFullAddress] = React.useState<IAddress | null>(null);
    const handleMapClose = React.useCallback(() => {
        setMapOpen(false);
    }, []);
    const handleMapOpen = React.useCallback(() => {
        setMapOpen(true);
    }, []);
    const validationSchema = React.useMemo(
        () =>
            Yup.object().shape({
                address: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                city: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                postalCode: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            }),
        [t]
    );
    const handleSubmit = React.useCallback(
        async (data: AddressFormData) => {
            setLoading(true);
            logUserEvent(EGAEventName.AddressEntryCreateRequested);
            if (fullAddress) {
                const addressBody = getAddressBodyFromAddressData(data);
                return onCreate({
                    name: data.label,
                    address: { ...addressBody, country: fullAddress.address.country },
                    note: data.note,
                    coordinates: fullAddress.coordinates
                });
            }
            try {
                const addressBody = getAddressBodyFromAddressData(data);
                logUserEvent(EGAEventName.AddressEntryValidateStarted);
                const addressObject = {
                    ...addressBody,
                    country: settings?.region?.code ?? 'UK'
                };
                const { position } = await addressApi.geocode(addressObject);
                logUserEvent(EGAEventName.AddressEntryValidateSuccess);
                onCreate({
                    name: data.label,
                    address: addressObject,
                    note: data.note,
                    coordinates: position
                });
            } catch (e: any) {
                setLoading(false);
                logUserEvent(EGAEventName.AddressEntryValidateError);
                showAlert('error', t('GENERAL_GENERIC_ERROR'), e?.data?.message);
            }
        },
        [fullAddress, logUserEvent, onCreate, settings?.region?.code, showAlert, t]
    );
    const handleLocationSelect = React.useCallback((data: IAddress) => {
        setFullAddress(data);
        setMapOpen(false);
    }, []);
    const { values, errors, setValues, submitForm, handleChange, isValid } = useFormik({
        initialValues: formInitialValues,
        onSubmit: handleSubmit,
        validationSchema
    });
    const handleEnterManually = React.useCallback(() => {
        setFullAddress(null);
    }, []);
    React.useEffect(() => {
        if (fullAddress) {
            setValues(currentValues => ({
                label: currentValues.label,
                address: fullAddress.address.line1,
                line2: fullAddress.address.line2 ?? '',
                city: fullAddress.address.city,
                region: fullAddress.address.region ?? '',
                postalCode: fullAddress.address.postalCode,
                country: fullAddress.address.country,
                note: currentValues.note
            }));
        }
    }, [setValues, fullAddress]);
    return (
        <Box sx={{ overflowY: 'auto', height: '100%', p: 1 }}>
            <FlatAppbar variant="dense">
                <FlatToolbar
                    title={t('DELIVERY_ADDRESS_ADD_TITLE')}
                    hideUserAvatar
                    disableEdit
                    onBack={onClose}
                />
            </FlatAppbar>
            <ButtonV2 fullWidth variant="contained" color="primary" onClick={handleMapOpen}>
                {t('DELIVERY_FIND_ON_MAP_BUTTON_TITLE')}
            </ButtonV2>
            <Box py={2}>
                <Typography>{t('DELIVERY_ADDRESS_ENTRY_MESSAGE')}</Typography>
            </Box>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <TextField
                        name="label"
                        label={t('DELIVERY_ADDRESS_NAME')}
                        onChange={handleChange}
                        value={values.label}
                        error={!!errors.label}
                        helperText={errors.label}
                        fullWidth
                        disabled={loading}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        disabled={loading}
                        name="address"
                        label={t('DELIVERY_ADDRESS_LINE_ONE')}
                        onChange={handleChange}
                        value={values.address}
                        error={!!errors.address}
                        helperText={errors.address}
                        fullWidth
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        disabled={loading}
                        name="line2"
                        label={t('DELIVERY_ADDRESS_LINE_TWO')}
                        onChange={handleChange}
                        value={values.line2}
                        error={!!errors.line2}
                        helperText={errors.line2}
                        fullWidth
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        disabled={loading}
                        name="city"
                        label={t('DELIVERY_ADDRESS_CITY')}
                        onChange={handleChange}
                        value={values.city}
                        error={!!errors.city}
                        helperText={errors.city}
                        fullWidth
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        disabled={loading}
                        name="region"
                        label={t('DELIVERY_ADDRESS_REGION')}
                        onChange={handleChange}
                        value={values.region}
                        error={!!errors.region}
                        helperText={errors.region}
                        fullWidth
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        disabled={loading}
                        name="postalCode"
                        label={t('DELIVERY_ADDRESS_POSTAL_CODE')}
                        onChange={handleChange}
                        value={values.postalCode}
                        error={!!errors.postalCode}
                        helperText={errors.postalCode}
                        fullWidth
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        name="note"
                        label={t('DELIVERY_ADDRESS_NOTE')}
                        onChange={handleChange}
                        value={values.note}
                        error={!!errors.note}
                        helperText={errors.note}
                        fullWidth
                    />
                </Grid>
                <Grid size={12}>
                    <ButtonV2
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={submitForm}
                        disabled={!isValid || loading}
                        loading={loading}
                    >
                        {t('BUTTON_SUBMIT')}
                    </ButtonV2>
                    {!!fullAddress && (
                        <ButtonV2
                            fullWidth
                            variant="text"
                            color="secondary"
                            onClick={handleEnterManually}
                            disabled={!isValid || loading}
                            loading={loading}
                            sx={{ mt: 0 }}
                        >
                            {t('DELIVERY_ADD_ADDRESS_MANUALLY')}
                        </ButtonV2>
                    )}
                </Grid>
            </Grid>
            <BaseModal fullScreen open={mapOpen} paperSx={{ p: 0, width: '100%' }}>
                <AddressMapPicker
                    onClose={handleMapClose}
                    onSelect={handleLocationSelect}
                    onError={showAlert}
                />
            </BaseModal>
            <NotificationAlert notification={currentNotification} onCancel={dismissAlert} />
        </Box>
    );
};
